import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { allCleared } from './alertsSlice';
import { AlertCard } from './AlertCard';
import { SeveritySummary } from './SeveritySummary';
import { countBySeverity, useActiveAlerts, useVisibleAlerts } from './selectors';

export function AlertsFeed() {
  const dispatch = useAppDispatch();
  const visible = useVisibleAlerts();
  const active = useActiveAlerts();
  const counts = countBySeverity(active);

  // Map-pin / cluster clicks set this id; scroll the matching card into view.
  const highlightedId = useAppSelector((s) => s.mapUi.highlightedAlertId);
  const cardRefs = useRef(new Map<string, HTMLDivElement>());
  useEffect(() => {
    if (!highlightedId) return;
    cardRefs.current.get(highlightedId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightedId]);

  const clearAll = () => dispatch(allCleared());

  return (
    <div
      style={{
        borderLeft: '1px solid var(--border)',
        background: 'var(--surface-panel)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      {/* header */}
      <div
        style={{
          padding: '16px 18px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
          <span className="eyebrow" style={{ fontSize: 13, fontWeight: 600, letterSpacing: 1.5 }}>
            Live Alerts
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>unacknowledged</span>
        </div>
        <span className="mono" style={{ fontSize: 20, fontWeight: 600, color: '#ff4d4d' }}>
          {active.length}
        </span>
      </div>

      <SeveritySummary counts={counts} />

      {/* list */}
      <div
        className="scrl"
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '2px 12px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          minHeight: 0,
        }}
      >
        {visible.map((alert) => (
          <AlertCard
            key={alert.id}
            alert={alert}
            highlighted={alert.id === highlightedId}
            registerRef={(el) => {
              if (el) cardRefs.current.set(alert.id, el);
              else cardRefs.current.delete(alert.id);
            }}
          />
        ))}
        {visible.length === 0 && (
          <div style={{ color: 'var(--text-dim)', fontSize: 12, padding: '12px 4px' }}>
            No active alerts. Start a rule to begin detection.
          </div>
        )}
      </div>

      {/* footer */}
      <div style={{ padding: 12, borderTop: '1px solid var(--border)', display: 'flex', gap: 8 }}>
        <button
          onClick={clearAll}
          style={{
            flex: 1,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,229,255,0.12)',
            border: '1px solid rgba(0,229,255,0.3)',
            borderRadius: 8,
            color: 'var(--accent)',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'var(--font-sans)',
          }}
        >
          Clear all
        </button>
        <div
          style={{
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--surface-card)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            color: 'var(--text-secondary)',
          }}
        >
          ⤓
        </div>
      </div>
    </div>
  );
}
